import api from "./api";
import type { ModulePermissionDto } from "../utils/rolePermissions";
import type { AuthUser } from "../stores/authStore";

export interface LoginData {
  email: string;
  password: string;
  totpCode?: string;
}

export type LoginResult =
  | AuthResponse
  | { requires2fa: true };

export interface RegisterData {
  name: string;
  email: string;
  password: string;
  phone: string;
  salesPointId: string;
  role?: string;
}

export interface AuthResponse {
  user: AuthUser & {
    createdAt?: string;
    updatedAt?: string;
  };
  accessToken: string;
  refreshToken: string;
}

function mapPermissionRows(raw: unknown): ModulePermissionDto[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((row: Record<string, unknown>) => ({
    module: String(row.module || ""),
    canCreate: Boolean(row.canCreate),
    canRead: Boolean(row.canRead),
    canUpdate: Boolean(row.canUpdate),
    canDelete: Boolean(row.canDelete),
    extraActions: Array.isArray(row.extraActions)
      ? row.extraActions.map(String)
      : [],
  }));
}

function mapUserFromDetail(
  detail: Record<string, unknown>,
  extras?: { permissions?: unknown; featurePermissions?: unknown }
): AuthResponse["user"] {
  const topFeatures = extras?.featurePermissions;
  const effective =
    Array.isArray(topFeatures)
      ? topFeatures.map(String)
      : Array.isArray(detail.effectiveFeaturePermissions)
        ? (detail.effectiveFeaturePermissions as string[])
        : [];

  return {
    id: String(detail.id),
    name: String(detail.name),
    email: String(detail.email),
    role: String(detail.role),
    branchId: detail.branchId ? String(detail.branchId) : undefined,
    salesPointId: String(
      detail.salesPointId || detail.departmentId || ""
    ),
    isActive: Boolean(detail.isActive),
    phone: detail.phone ? String(detail.phone) : "",
    createdAt: detail.createdAt ? String(detail.createdAt) : undefined,
    updatedAt: detail.updatedAt ? String(detail.updatedAt) : undefined,
    featurePermissions:
      (detail.featurePermissions as Record<string, boolean>) || undefined,
    effectiveFeaturePermissions: effective,
    permissions: mapPermissionRows(extras?.permissions ?? detail.permissions),
  };
}

export const authAPI = {
  login: async (data: LoginData): Promise<LoginResult> => {
    const response = await api.post("/auth/login", data);
    const apiData = response.data.data;

    if (apiData?.requires2fa) {
      return { requires2fa: true };
    }

    return {
      user: mapUserFromDetail(apiData.userDetail, {
        permissions: apiData.permissions,
        featurePermissions: apiData.featurePermissions,
      }),
      accessToken: apiData.accessToken,
      refreshToken: apiData.refreshToken || "",
    };
  },

  register: async (data: RegisterData): Promise<AuthResponse> => {
    const response = await api.post("/auth/register", data);
    return response.data.data;
  },

  refreshToken: async (
    refreshToken: string
  ): Promise<{ accessToken: string }> => {
    const response = await api.post("/auth/refresh-token", { refreshToken });
    return response.data.data;
  },

  getMe: async (): Promise<AuthUser> => {
    const response = await api.get("/auth/me");
    const apiData = response.data.data;

    return mapUserFromDetail(apiData, {
      permissions: apiData.permissions,
      featurePermissions: apiData.featurePermissions,
    });
  },

  changePassword: async (data: {
    currentPassword: string;
    newPassword: string;
  }) => {
    const response = await api.post("/auth/change-password", data);
    return response.data;
  },

  updateUser: async (data: { name: string; email: string; phone?: string }) => {
    const response = await api.put("/auth/profile", data);
    return response.data;
  },

  logout: async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore network errors during logout */
    }
    localStorage.clear();
  },

  get2faStatus: async (): Promise<{ required: boolean; enabled: boolean }> => {
    const response = await api.get("/auth/2fa/status");
    return response.data.data;
  },

  setup2fa: async (): Promise<{
    secret: string;
    otpauthUrl: string;
  }> => {
    const response = await api.post("/auth/2fa/setup");
    return response.data.data;
  },

  confirm2fa: async (totpCode: string): Promise<{ enabled: boolean }> => {
    const response = await api.post("/auth/2fa/confirm", { totpCode });
    return response.data.data;
  },

  disable2fa: async (payload: {
    password: string;
    totpCode: string;
  }): Promise<{ enabled: boolean }> => {
    const response = await api.post("/auth/2fa/disable", payload);
    return response.data.data;
  },
};
